import { MOUSE_DEAD_ZONE_PX, SPECTATOR_EDGE_PAN_CSS_PX } from '../game/constants/feel';
import {
  IDLE_INTENT_STATE,
  toWorld,
  type Intent,
  type InputContext,
  type InputSource,
  type IntentState,
} from './intent';

type PanAxis = 0 | 1 | 2 | 3;

const ARROW_PAN: Readonly<Record<string, PanAxis>> = {
  ArrowLeft: 0,
  ArrowUp: 1,
  ArrowRight: 2,
  ArrowDown: 3,
};

/** Every one of these is a player action too, so the release path must fall through to the action switch whenever the viewer is not a spectator. */
const WASD_PAN: Readonly<Record<string, PanAxis>> = {
  KeyA: 0,
  KeyW: 1,
  KeyD: 2,
  KeyS: 3,
};

function isTextEntryTarget(target: EventTarget | null): boolean {
  const element = target as { tagName?: string; isContentEditable?: boolean } | null;
  if (!element) return false;
  if (element.isContentEditable) return true;
  switch (element.tagName) {
    case 'INPUT':
    case 'TEXTAREA':
    case 'SELECT':
      return true;
    default:
      return false;
  }
}

export class PointerKeyboardSource implements InputSource {
  private pointerX = 0;
  private pointerY = 0;
  private hasPointer = false;
  private firing = false;
  private braking = false;
  private readonly pending: Intent[] = [];
  private readonly keyAxes: [boolean, boolean, boolean, boolean] = [false, false, false, false];
  private readonly edgeAxes: [boolean, boolean, boolean, boolean] = [false, false, false, false];
  private lastState: IntentState = IDLE_INTENT_STATE;
  private spectating = false;
  private edgePanArmed = false;
  private lastContext: InputContext | null = null;

  private readonly onPointerMove = (event: PointerEvent): void => {
    this.pointerX = event.clientX;
    this.pointerY = event.clientY;
    this.hasPointer = true;
    this.edgePanArmed = true;
  };

  /** The edge band is a position, not an act, and a position outlives the pointer: with no leave the last in-window coordinate keeps panning forever, and a follow-click landing in the band would be undone by the pan before the next frame. */
  private readonly onPointerLeave = (): void => {
    this.edgePanArmed = false;
    this.edgeAxes.fill(false);
  };

  /** mousedown, not pointerdown: Chromium emits no pointerdown for a second button while one is held. */
  private readonly onMouseDown = (event: MouseEvent): void => {
    this.pointerX = event.clientX;
    this.pointerY = event.clientY;
    this.hasPointer = true;

    const consumed = this.hudClick?.(event.clientX, event.clientY, event.button) ?? null;
    if (consumed !== null) {
      this.pending.push(consumed);
      return;
    }

    if (this.spectating) {
      if (event.button === 0) this.queuePickFollow(event.clientX, event.clientY);
      return;
    }

    if (event.button === 0) {
      this.firing = true;
      this.pending.push({ kind: 'fireOnce' });
    } else if (event.button === 2) {
      this.queueAbilityAtPointer();
    }
  };

  private readonly onMouseUp = (event: MouseEvent): void => {
    if (event.button === 0) this.firing = false;
  };

  private readonly onContextMenu = (event: Event): void => {
    event.preventDefault();
  };

  /** Non-passive, and only swallowed while spectating: taking the wheel from a player would take their page scroll with nothing offered back. */
  private readonly onWheel = (event: WheelEvent): void => {
    if (!this.spectating) return;
    event.preventDefault();
    const ratio = this.lastContext?.pixelRatio ?? 1;
    this.pending.push({
      kind: 'zoomView',
      steps: Math.sign(event.deltaY),
      deviceX: event.clientX * ratio,
      deviceY: event.clientY * ratio,
    });
  };

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (isTextEntryTarget(event.target)) return;
    if (event.ctrlKey || event.metaKey || event.altKey) return;

    if (this.spectating) {
      if (!this.setPanKey(event.code, true)) return;
      event.preventDefault();
      return;
    }

    switch (event.code) {
      case 'Digit0':
      case 'Digit1':
      case 'Digit2':
      case 'Digit3':
      case 'Digit4':
      case 'Digit5':
      case 'Digit6':
      case 'Digit7':
      case 'Digit8':
      case 'Digit9':
        this.pending.push({ kind: 'sendEmoji', index: Number(event.code.slice(5)) });
        break;

      case 'KeyQ':
        this.pending.push({ kind: 'buyUpgrade', path: 0 });
        break;
      case 'KeyW':
        this.pending.push({ kind: 'buyUpgrade', path: 1 });
        break;
      case 'KeyE':
        this.pending.push({ kind: 'buyUpgrade', path: 2 });
        break;

      case 'KeyR':
        this.pending.push({ kind: 'toggleUpgradeScreen' });
        break;

      case 'KeyA':
        this.firing = true;
        this.pending.push({ kind: 'fireOnce' });
        break;
      case 'KeyS':
        this.queueAbilityAtPointer();
        break;

      case 'ShiftLeft':
      case 'ShiftRight':
        this.braking = true;
        break;

      case 'Escape':
        this.pending.push({ kind: 'suicide' });
        break;

      default:
        return;
    }

    event.preventDefault();
  };

  private readonly onKeyUp = (event: KeyboardEvent): void => {
    if (this.setPanKey(event.code, false)) return;
    switch (event.code) {
      case 'KeyA':
        this.firing = false;
        break;
      case 'ShiftLeft':
      case 'ShiftRight':
        this.braking = false;
        break;
      default:
        break;
    }
  };

  private readonly onBlur = (): void => {
    this.firing = false;
    this.braking = false;
    this.keyAxes.fill(false);
    this.edgeAxes.fill(false);
    this.edgePanArmed = false;
  };

  constructor(
    private readonly canvas: HTMLElement,
    private readonly hudClick?: (x: number, y: number, button: number) => Intent | null,
    private readonly blocked?: () => boolean,
  ) {
    canvas.addEventListener('pointermove', this.onPointerMove);
    canvas.addEventListener('mousedown', this.onMouseDown);
    canvas.addEventListener('wheel', this.onWheel, { passive: false });
    canvas.addEventListener('pointerleave', this.onPointerLeave);
    /** Up and blur go on the window: a button released outside the canvas must still clear the held state. */
    window.addEventListener('mouseup', this.onMouseUp);
    window.addEventListener('contextmenu', this.onContextMenu);
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);
  }

  sample(context: InputContext): IntentState {
    this.lastContext = context;
    this.spectating = context.spectating;
    this.sampleEdgePan(context);

    if (this.blocked?.() === true) {
      this.firing = false;
      this.lastState = { ...this.lastState, firing: false, moving: false, braking: false };
      return this.lastState;
    }

    if (!this.hasPointer) {
      this.lastState = {
        ...this.lastState,
        firing: this.firing,
        braking: this.braking,
        moving: false,
        panAxes: this.panAxes(),
      };
      return this.lastState;
    }

    const pointerX = this.pointerX * context.pixelRatio;
    const pointerY = this.pointerY * context.pixelRatio;
    const offsetX = pointerX - context.viewportWidth / 2;
    const offsetY = pointerY - context.viewportHeight / 2;

    const aimAngle = Math.atan2(offsetY, offsetX);

    const moving = Math.abs(offsetX) > MOUSE_DEAD_ZONE_PX || Math.abs(offsetY) > MOUSE_DEAD_ZONE_PX;

    const world = toWorld(pointerX, pointerY, context);

    this.lastState = {
      aimAngle,
      moving,
      firing: this.firing,
      braking: this.braking,
      pointerWorldX: world.x,
      pointerWorldY: world.y,
      panAxes: this.panAxes(),
    };
    return this.lastState;
  }

  private panAxes(): [boolean, boolean, boolean, boolean] {
    return [
      this.keyAxes[0] || this.edgeAxes[0],
      this.keyAxes[1] || this.edgeAxes[1],
      this.keyAxes[2] || this.edgeAxes[2],
      this.keyAxes[3] || this.edgeAxes[3],
    ];
  }

  /** Measured in CSS pixels, because the margin is a physical distance the hand aims at, not a fraction of the render buffer. */
  private sampleEdgePan(context: InputContext): void {
    if (!context.spectating || !this.hasPointer || !this.edgePanArmed) {
      this.edgeAxes.fill(false);
      return;
    }
    const width = context.viewportWidth / context.pixelRatio;
    const height = context.viewportHeight / context.pixelRatio;
    const margin = SPECTATOR_EDGE_PAN_CSS_PX;
    this.edgeAxes[0] = this.pointerX <= margin;
    this.edgeAxes[1] = this.pointerY <= margin;
    this.edgeAxes[2] = this.pointerX >= width - margin;
    this.edgeAxes[3] = this.pointerY >= height - margin;
  }

  private setPanKey(code: string, down: boolean): boolean {
    const arrow = ARROW_PAN[code];
    if (arrow !== undefined) {
      this.keyAxes[arrow] = down;
      return true;
    }
    if (!this.spectating) return false;
    const wasd = WASD_PAN[code];
    if (wasd === undefined) return false;
    this.keyAxes[wasd] = down;
    return true;
  }

  private queuePickFollow(clientX: number, clientY: number): void {
    const context = this.lastContext;
    if (context === null) return;
    const world = toWorld(clientX * context.pixelRatio, clientY * context.pixelRatio, context);
    this.edgePanArmed = false;
    this.edgeAxes.fill(false);
    this.pending.push({ kind: 'pickFollow', worldX: world.x, worldY: world.y });
  }

  drain(): Intent[] {
    if (this.pending.length === 0) return [];
    return this.pending.splice(0, this.pending.length);
  }

  dispose(): void {
    this.canvas.removeEventListener('pointermove', this.onPointerMove);
    this.canvas.removeEventListener('mousedown', this.onMouseDown);
    this.canvas.removeEventListener('wheel', this.onWheel);
    this.canvas.removeEventListener('pointerleave', this.onPointerLeave);
    window.removeEventListener('mouseup', this.onMouseUp);
    window.removeEventListener('contextmenu', this.onContextMenu);
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
  }

  private queueAbilityAtPointer(): void {
    this.pending.push({
      kind: 'useAbilityAt',
      worldX: this.lastState.pointerWorldX,
      worldY: this.lastState.pointerWorldY,
    });
  }
}
